import { useState, useMemo, useEffect } from "react";
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Table,
  Button,
  Select,
  TextInput,
  Grid,
  ActionIcon,
  Tooltip,
  Modal,
  SimpleGrid,
  ThemeIcon,
} from "@mantine/core";
import {
  IconEye,
  IconCheck,
  IconX,
  IconClock,
  IconCurrencyDollar,
  IconShoppingCart,
  IconFilter,
  IconFileInvoice,
  IconTruck,
  IconSearch,
} from "@tabler/icons-react";
import { DatePickerInput } from "@mantine/dates";
import { useAuth } from "../Context/useAuth";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useOrders, useOrderStats } from "../hooks/useOrders";
import { useInvoices } from "../hooks/useInvoices";
import { fetchOrderDetails } from "../Services/order-services";
import { generateManualInvoice } from "../Services/invoice-services";
import { getInvoiceGenerationError } from "../utils/vatDisplay";
import { OrderStatus, OrderQueryParams } from "../types/order.types";
import CountUp from "react-countup";

// Statuses that are eligible for invoice generation
const INVOICEABLE_STATUSES = new Set([
  "SHIPPED",
  "Shipped",
  "DELIVERED",
  "Delivered",
  "PARTIALLY_SHIPPED",
  "PartiallyShipped",
]);

const getStatusColor = (status: OrderStatus): string => {
  switch (status) {
    case "PENDING":
    case "UNSHIPPED":
    case "PENDING_AVAILABILITY":
      return "orange";
    case "SHIPPED":
    case "PARTIALLY_SHIPPED":
      return "blue";
    case "DELIVERED":
      return "green";
    case "CANCELLED":
    case "UNFULFILLABLE":
      return "red";
    case "INVOICE_UNCONFIRMED":
      return "yellow";
    default:
      return "gray";
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount: number, currency: string): string => {
  return `${currency} ${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export default function OrdersPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  // ── SP-API page-by-page pagination (nextToken) ───────────────────────────
  // tokens[0] = undefined (first page); tokens[n] = nextToken used to fetch page n+1
  const [pageTokens, setPageTokens] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [pageIndex, setPageIndex] = useState(0);

  // ── Filter controls (Inputs) ─────────────
  const [statusInput, setStatusInput] = useState<OrderStatus | null>(null);
  const [orderIdInput, setOrderIdInput] = useState<string>("");
  const [dateRangeInput, setDateRangeInput] = useState<
    [Date | null, Date | null]
  >([null, null]);

  // ── Applied Filters (Used by useMemo) ────
  const [appliedFilters, setAppliedFilters] = useState({
    status: null as OrderStatus | null,
    orderId: "",
    dateRange: [null, null] as [Date | null, Date | null],
  });

  // Order details modal state
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [selectedOrderItems, setSelectedOrderItems] = useState<any[]>([]);
  const [detailsModalOpened, setDetailsModalOpened] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);

  // Client only sends nextToken; backend owns createdAfter/createdBefore
  const orderQueryParams = useMemo<OrderQueryParams>(() => {
    const nextToken = pageTokens[pageIndex];
    return nextToken ? { nextToken } : {};
  }, [pageTokens, pageIndex]);

  // ── React Query: one SP-API page + invoices in parallel ──────────────────
  const {
    data: ordersData,
    isFetching: loading,
    error: ordersError,
    isError: ordersIsError,
  } = useOrders(orderQueryParams);
  const { data: stats } = useOrderStats();
  const { invoices: existingInvoices } = useInvoices({ limit: 9999 });

  const invoicedOrderIds = useMemo(
    () =>
      new Set(
        existingInvoices.map((inv: any) => inv.amazonOrderId).filter(Boolean),
      ),
    [existingInvoices],
  );

  /**
   * Returns true only when invoice generation is allowed:
   *   1. Order status is Shipped / Delivered / PartiallyShipped
   *   2. No invoice already exists for this order
   */
  const canGenerateInvoice = (
    order: any,
  ): { allowed: boolean; reason: string } => {
    const status =
      order.fulfillment?.fulfillmentStatus || order.orderStatus || "";
    const orderId = order.orderId || order.amazonOrderId || "";

    if (invoicedOrderIds.has(orderId)) {
      return { allowed: false, reason: "Invoice already generated" };
    }
    if (!INVOICEABLE_STATUSES.has(status)) {
      return {
        allowed: false,
        reason: `Cannot invoice ${status || "unknown"} orders`,
      };
    }
    return { allowed: true, reason: "Generate Invoice" };
  };

  // Raw SP-API order objects — unchanged from backend payload
  const rawOrders = ordersData?.payload?.orders ?? [];
  const hasMore = Boolean(
    ordersData?.payload?.hasMore ?? ordersData?.payload?.nextToken,
  );

  // ── Client-side filtering on the current SP-API page only ───────────────
  const filteredOrders = useMemo(() => {
    let result = [...rawOrders];

    if (appliedFilters.status) {
      result = result.filter((o) => {
        const status = o.fulfillment?.fulfillmentStatus || o.orderStatus;
        return status === appliedFilters.status;
      });
    }

    if (appliedFilters.orderId.trim()) {
      const search = appliedFilters.orderId.trim().toLowerCase();
      result = result.filter((o) => o.orderId?.toLowerCase().includes(search));
    }

    if (appliedFilters.dateRange[0]) {
      const from = new Date(appliedFilters.dateRange[0]);
      from.setHours(0, 0, 0, 0);
      result = result.filter((o) => new Date(o.createdTime) >= from);
    }

    if (appliedFilters.dateRange[1]) {
      const to = new Date(appliedFilters.dateRange[1]);
      to.setHours(23, 59, 59, 999);
      result = result.filter((o) => new Date(o.createdTime) <= to);
    }

    // Always sort descending (newest first)
    result.sort(
      (a, b) =>
        new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime(),
    );

    return result;
  }, [rawOrders, appliedFilters]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleApplyFilters = () => {
    setAppliedFilters({
      status: statusInput,
      orderId: orderIdInput,
      dateRange: dateRangeInput,
    });
  };

  const handleClearFilters = () => {
    setStatusInput(null);
    setOrderIdInput("");
    setDateRangeInput([null, null]);
    setAppliedFilters({ status: null, orderId: "", dateRange: [null, null] });
  };

  const handleNextPage = () => {
    const nextToken = ordersData?.payload?.nextToken;
    if (!nextToken) return;

    setPageTokens((prev) => {
      const trimmed = prev.slice(0, pageIndex + 1);
      return [...trimmed, nextToken];
    });
    setPageIndex((i) => i + 1);
  };

  const handlePrevPage = () => {
    setPageIndex((i) => Math.max(0, i - 1));
  };

  useEffect(() => {
    if (!ordersIsError || !ordersError) return;
    const code = (ordersError as { response?: { data?: { code?: string } } })
      ?.response?.data?.code;
    if (code !== "PAGINATION_SESSION_EXPIRED") return;

    setPageTokens([undefined]);
    setPageIndex(0);
    queryClient.invalidateQueries({ queryKey: ["orders"] });
    notifications.show({
      title: "Order list refreshed",
      message:
        "Pagination expired (for example after a server restart). Showing page 1 again.",
      color: "yellow",
    });
  }, [ordersIsError, ordersError, queryClient]);

  const handleViewDetails = async (orderId: string) => {
    if (!token) return;

    try {
      setDetailsLoading(true);
      setDetailsModalOpened(true);
      const response = await fetchOrderDetails(token, orderId);
      // SP-API 2026-01-01: payload.order contains the order; orderItems is nested inside it
      const orderObj = response.payload?.order ?? response.payload;
      // orderItems may be inside the order object itself
      const items = orderObj?.orderItems || response.payload?.orderItems || [];
      setSelectedOrder(orderObj);
      setSelectedOrderItems(items);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to load order details",
        color: "red",
      });
      setDetailsModalOpened(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleGenerateInvoice = async (orderId: string) => {
    if (!token) return;

    try {
      setGeneratingId(orderId);
      await generateManualInvoice(token, orderId);
      // Invalidate invoices cache so the button disables immediately after success
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      notifications.show({
        title: "Success",
        message: "Invoice generated successfully",
        color: "green",
        icon: <IconCheck size={16} />,
      });
    } catch (error: any) {
      let parsedError = error;

      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          parsedError = {
            response: { data: JSON.parse(text) },
          };
        } catch (e) {}
      }

      const { title, message } = getInvoiceGenerationError(parsedError);

      notifications.show({
        title,
        message,
        color: "red",
        icon: <IconX size={16} />,
      });
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <>
    <Container fluid py="xl">
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between">
            <div>
              <Title order={2}>Orders</Title>
              <Text c="dimmed" size="sm">
                View and manage your Amazon orders
              </Text>
            </div>
          </Group>

          {/* Statistics */}
          {stats && (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
              <Paper p="md" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Total Orders
                    </Text>
                    <Text fw={700} size="xl" mt="xs">
                      <CountUp end={stats.totalOrders} duration={1.5} />
                    </Text>
                  </div>
                  <ThemeIcon color="blue" size={44} radius="md" variant="light">
                    <IconShoppingCart size={24} stroke={1.5} />
                  </ThemeIcon>
                </Group>
              </Paper>

              <Paper p="md" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Pending
                    </Text>
                    <Text fw={700} size="xl" mt="xs">
                      <CountUp end={stats.pendingOrders} duration={1.5} />
                    </Text>
                  </div>
                  <ThemeIcon
                    color="orange"
                    size={44}
                    radius="md"
                    variant="light"
                  >
                    <IconClock size={24} stroke={1.5} />
                  </ThemeIcon>
                </Group>
              </Paper>

              <Paper p="md" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Shipped
                    </Text>
                    <Text fw={700} size="xl" mt="xs">
                      <CountUp end={stats.shippedOrders} duration={1.5} />
                    </Text>
                  </div>
                  <ThemeIcon color="blue" size={44} radius="md" variant="light">
                    <IconTruck size={24} stroke={1.5} />
                  </ThemeIcon>
                </Group>
              </Paper>

              <Paper p="md" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Total Revenue
                    </Text>
                    <Text fw={700} size="lg" mt="xs">
                      {stats.currencyCode}{" "}
                      <CountUp
                        end={stats.totalRevenue}
                        duration={1.5}
                        decimals={2}
                        separator=","
                      />
                    </Text>
                  </div>
                  <ThemeIcon color="teal" size={44} radius="md" variant="light">
                    <IconCurrencyDollar size={24} stroke={1.5} />
                  </ThemeIcon>
                </Group>
              </Paper>
            </SimpleGrid>
          )}

          {/* Filters */}
          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              <Text fw={600}>Filters</Text>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Select
                    label="Order Status"
                    placeholder="All statuses"
                    value={statusInput}
                    onChange={(value) => setStatusInput(value as OrderStatus)}
                    data={[
                      { value: "PENDING", label: "Pending" },
                      { value: "UNSHIPPED", label: "Unshipped" },
                      {
                        value: "PARTIALLY_SHIPPED",
                        label: "Partially Shipped",
                      },
                      { value: "SHIPPED", label: "Shipped" },
                      { value: "DELIVERED", label: "Delivered" },
                      { value: "CANCELLED", label: "Cancelled" },
                      { value: "UNFULFILLABLE", label: "Unfulfillable" },
                      {
                        value: "INVOICE_UNCONFIRMED",
                        label: "Invoice Unconfirmed",
                      },
                    ]}
                    clearable
                  />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <TextInput
                    label="Order ID"
                    placeholder="Search by Amazon Order ID"
                    value={orderIdInput}
                    onChange={(e) => setOrderIdInput(e.currentTarget.value)}
                    leftSection={<IconSearch size={16} />}
                    onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                  />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                  <DatePickerInput
                    type="range"
                    label="Purchase Date Range"
                    placeholder="Select date range"
                    value={dateRangeInput}
                    onChange={(val) =>
                      setDateRangeInput(val as [Date | null, Date | null])
                    }
                    clearable
                  />
                </Grid.Col>
              </Grid>

              <Group justify="flex-end">
                <Button variant="subtle" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
                <Button
                  leftSection={<IconFilter size={16} />}
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </Button>
              </Group>
            </Stack>
          </Paper>

          {/* Orders Table */}
          <Paper radius="md" withBorder>
            <Table.ScrollContainer minWidth={800}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Order ID</Table.Th>
                    <Table.Th>Purchase Date</Table.Th>
                    <Table.Th>Customer</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Items</Table.Th>
                    <Table.Th>Total</Table.Th>
                    <Table.Th>Fulfillment</Table.Th>
                    <Table.Th>Marketplace</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {loading ? (
                    <Table.Tr>
                      <Table.Td colSpan={9}>
                        <Text ta="center" py="xl">
                          Loading orders...
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : filteredOrders.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={9}>
                        <Text ta="center" py="xl" c="dimmed">
                          {rawOrders.length > 0
                            ? "No orders match your filters"
                            : "No orders found"}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <Table.Tr key={order.orderId}>
                        <Table.Td>
                          <Group>
                            <Text fw={600} size="sm">
                              {order.orderId}
                            </Text>
                            {order.isPrime && (
                              <Badge size="xs" color="blue" mt={4}>
                                Prime
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatDate(order.createdTime)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {order.buyer?.buyerName || order.buyer?.name || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {/* SP-API list: status is at fulfillment.fulfillmentStatus */}
                          {(() => {
                            const status =
                              order.fulfillment?.fulfillmentStatus ||
                              order.orderStatus;
                            return (
                              <Badge
                                color={getStatusColor(status as any)}
                                variant="filled"
                              >
                                {status || "—"}
                              </Badge>
                            );
                          })()}
                        </Table.Td>
                        <Table.Td>
                          {/* Item count: sum quantityOrdered across orderItems */}
                          <Text size="sm">
                            {order.orderItems
                              ? order.orderItems.reduce(
                                  (sum: number, i: any) =>
                                    sum + (i.quantityOrdered || 1),
                                  0,
                                )
                              : (order.numberOfItemsShipped ?? 0) +
                                (order.numberOfItemsUnshipped ?? 0)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {/* SP-API list: total is at proceeds.grandTotal */}
                          <Text fw={600} size="sm">
                            {(() => {
                              const t =
                                order.proceeds?.grandTotal || order.orderTotal;
                              return t
                                ? formatCurrency(
                                    Number(t.amount),
                                    t.currencyCode,
                                  )
                                : "—";
                            })()}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {/* SP-API list: fulfilledBy is at fulfillment.fulfilledBy */}
                          <Badge variant="outline">
                            {order.fulfillment?.fulfilledBy ||
                              order.fulfillmentChannel ||
                              "—"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light">
                            {order.salesChannel?.marketplaceName ||
                              order.salesChannel?.marketplaceId ||
                              "—"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="View Details">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleViewDetails(order.orderId)}
                              >
                                <IconEye size={18} />
                              </ActionIcon>
                            </Tooltip>
                            {/* Generate Invoice — disabled if already invoiced or not a shippable status */}
                            {(() => {
                              const { allowed, reason } =
                                canGenerateInvoice(order);
                              return (
                                <Tooltip label={reason}>
                                  {/* span wrapper required — Mantine Tooltip needs a non-disabled child */}
                                  <span>
                                    <ActionIcon
                                      variant="subtle"
                                      color={allowed ? "green" : "gray"}
                                      disabled={
                                        !allowed ||
                                        generatingId === order.orderId
                                      }
                                      loading={generatingId === order.orderId}
                                      onClick={() =>
                                        allowed &&
                                        handleGenerateInvoice(order.orderId)
                                      }
                                    >
                                      <IconFileInvoice size={18} />
                                    </ActionIcon>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>

          {/* SP-API nextToken pagination */}
          {(pageIndex > 0 || hasMore) && (
            <Group justify="space-between" mt="md">
              <Text size="sm" c="dimmed">
                Page {pageIndex + 1}
                {filteredOrders.length > 0 &&
                  ` · ${filteredOrders.length} orders`}
                {hasMore ? " · more available" : ""}
              </Text>
              <Group>
                <Button
                  variant="default"
                  disabled={pageIndex <= 0 || loading}
                  onClick={handlePrevPage}
                >
                  Previous
                </Button>
                <Button
                  variant="default"
                  disabled={!hasMore || loading}
                  onClick={handleNextPage}
                >
                  Next
                </Button>
              </Group>
            </Group>
          )}
        </Stack>
      </Container>

      {/* Order Details Modal */}
      <Modal
        opened={detailsModalOpened}
        onClose={() => setDetailsModalOpened(false)}
        title="Order Details"
        size="xl"
        padding="lg"
      >
        {detailsLoading || !selectedOrder ? (
          <Text>Loading order details...</Text>
        ) : (
          <Stack gap="lg">
            {/* Order Header */}
            <Paper p="md" withBorder>
              <Group justify="space-between" mb="md">
                <div>
                  <Text fw={700} size="lg">
                    {selectedOrder.orderId}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Purchased: {formatDate(selectedOrder.createdTime)}
                  </Text>
                  {selectedOrder.lastUpdatedTime && (
                    <Text size="sm" c="dimmed">
                      Last Updated: {formatDate(selectedOrder.lastUpdatedTime)}
                    </Text>
                  )}
                  {(selectedOrder.buyer?.buyerName ||
                    selectedOrder.buyer?.name) && (
                    <Text size="sm" c="dimmed">
                      Buyer:{" "}
                      {selectedOrder.buyer?.buyerName ||
                        selectedOrder.buyer?.name}
                    </Text>
                  )}
                </div>
                {/* SP-API detail: status lives at fulfillment.fulfillmentStatus */}
                {(() => {
                  const status =
                    selectedOrder.orderStatus ||
                    selectedOrder.fulfillment?.fulfillmentStatus;
                  return status ? (
                    <Badge
                      size="lg"
                      color={getStatusColor(status as any)}
                      variant="filled"
                    >
                      {status}
                    </Badge>
                  ) : null;
                })()}
              </Group>

              <Grid mt="xs" gutter="sm">
                {/* Sales Channel */}
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Marketplace
                  </Text>
                  <Badge variant="light" color="blue" mt={4}>
                    {selectedOrder.salesChannel?.marketplaceName || "—"}
                  </Badge>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Channel
                  </Text>
                  <Badge variant="dot" color="green" mt={4}>
                    {selectedOrder.salesChannel?.channelName || "—"}
                  </Badge>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Marketplace ID
                  </Text>
                  <Text size="sm" ff="monospace" mt={4}>
                    {selectedOrder.salesChannel?.marketplaceId || "—"}
                  </Text>
                </Grid.Col>

                {/* Fulfillment */}
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Fulfilled By
                  </Text>
                  <Badge variant="outline" mt={4}>
                    {selectedOrder.fulfillment?.fulfilledBy ||
                      selectedOrder.fulfillmentChannel ||
                      "—"}
                  </Badge>
                </Grid.Col>
                {selectedOrder.fulfillment?.fulfillmentServiceLevel && (
                  <Grid.Col span={6}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Service Level
                    </Text>
                    <Badge variant="outline" color="orange" mt={4}>
                      {selectedOrder.fulfillment.fulfillmentServiceLevel}
                    </Badge>
                  </Grid.Col>
                )}
                {selectedOrder.fulfillment?.shipByWindow?.latestDateTime && (
                  <Grid.Col span={6}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Ship By
                    </Text>
                    <Text size="sm" mt={4}>
                      {formatDate(
                        selectedOrder.fulfillment.shipByWindow.latestDateTime,
                      )}
                    </Text>
                  </Grid.Col>
                )}
              </Grid>
            </Paper>

            {/* Recipient / Shipping Address */}
            {selectedOrder.recipient && (
              <Paper p="md" withBorder>
                <Text fw={600} mb="sm">
                  Shipping Address
                </Text>
                {/* SP-API 2026-01-01: address is under deliveryAddress */}
                {(() => {
                  const addr =
                    selectedOrder.recipient.deliveryAddress ||
                    selectedOrder.recipient.address;
                  return (
                    <Stack gap="xs">
                      {addr?.name && (
                        <Text size="sm" fw={500}>
                          {addr.name}
                        </Text>
                      )}
                      {addr?.addressLine1 && (
                        <Text size="sm">{addr.addressLine1}</Text>
                      )}
                      {(addr?.city || addr?.postalCode) && (
                        <Text size="sm">
                          {[addr?.city, addr?.postalCode]
                            .filter(Boolean)
                            .join(", ")}
                        </Text>
                      )}
                      {addr?.districtOrCounty && (
                        <Text size="sm" c="dimmed">
                          {addr.districtOrCounty}
                        </Text>
                      )}
                      {addr?.countryCode && (
                        <Text size="sm">{addr.countryCode}</Text>
                      )}
                    </Stack>
                  );
                })()}
              </Paper>
            )}

            {/* Order Items */}
            <Paper p="md" withBorder>
              <Text fw={600} mb="md">
                Order Items
              </Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Product</Table.Th>
                    <Table.Th>SKU</Table.Th>
                    <Table.Th>Quantity</Table.Th>
                    <Table.Th>Price</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {selectedOrderItems.map((item) => {
                    // SP-API 2026-01-01: product info is nested under item.product
                    const product = item.product || item;
                    const price =
                      product.price?.unitPrice ||
                      item.proceeds?.proceedsTotal ||
                      item.itemPrice;
                    const title =
                      product.title ||
                      item.title ||
                      item.productTitle ||
                      product.asin;
                    const asin = product.asin || item.asin;
                    const sku = product.sellerSku || item.sellerSku || item.sku;

                    return (
                      <Table.Tr key={item.orderItemId}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {title}
                          </Text>
                          <Text size="xs" c="dimmed">
                            ASIN: {asin}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{sku || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{item.quantityOrdered}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={600}>
                            {price
                              ? formatCurrency(
                                  parseFloat(price.amount),
                                  price.currencyCode,
                                )
                              : "—"}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Paper>

            {/* Price Breakdown — SP-API 2026-01-01 proceeds.breakdowns */}
            {(() => {
              // Collect all breakdowns: prefer item-level, fall back to order-level
              const allBreakdowns: any[] = [];
              selectedOrderItems.forEach((item) => {
                if (item.proceeds?.breakdowns?.length) {
                  item.proceeds.breakdowns.forEach((b: any) => {
                    allBreakdowns.push({ ...b, _itemId: item.orderItemId });
                  });
                }
              });
              // If no item-level breakdowns, try order-level
              const breakdowns =
                allBreakdowns.length > 0
                  ? allBreakdowns
                  : selectedOrder.proceeds?.breakdowns || [];

              const grandTotal =
                selectedOrder.proceeds?.grandTotal || selectedOrder.orderTotal;

              if (!breakdowns.length && !grandTotal) return null;

              const isDeduction = (type: string) =>
                ["DISCOUNT", "PROMOTION", "COUPON"].includes(
                  type?.toUpperCase(),
                );

              const typeLabel: Record<string, string> = {
                ITEM: "Items Subtotal",
                SHIPPING: "Shipping",
                GIFT_WRAP: "Gift Wrap",
                DISCOUNT: "Discount",
                PROMOTION: "Promotion",
                COUPON: "Coupon",
                TAX: "Tax",
                FEE: "Fee",
              };

              return (
                <Paper p="md" withBorder>
                  <Text fw={600} mb="sm">
                    Price Breakdown
                  </Text>
                  <Stack gap={6}>
                    {breakdowns.map((b: any, i: number) => {
                      const deduction = isDeduction(b.type);
                      const label = typeLabel[b.type?.toUpperCase()] || b.type;
                      const amt = parseFloat(b.subtotal?.amount || "0");
                      const currency = b.subtotal?.currencyCode || "";

                      return (
                        <div key={i}>
                          <Group justify="space-between">
                            <Group gap={6}>
                              <Text size="sm" c={deduction ? "red" : "inherit"}>
                                {label}
                              </Text>
                              {deduction && (
                                <Badge size="xs" color="red" variant="light">
                                  deduction
                                </Badge>
                              )}
                            </Group>
                            <Text
                              size="sm"
                              fw={500}
                              c={deduction ? "red" : "inherit"}
                            >
                              {deduction ? "−" : ""}
                              {formatCurrency(amt, currency)}
                            </Text>
                          </Group>
                          {/* Detailed sub-breakdowns (e.g. DISCOUNT → SHIPPING) */}
                          {b.detailedBreakdowns?.map((d: any, j: number) => (
                            <Group
                              key={j}
                              justify="space-between"
                              pl="md"
                              mt={2}
                            >
                              <Text size="xs" c="dimmed">
                                └ {d.subtype}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {formatCurrency(
                                  parseFloat(d.value?.amount || "0"),
                                  d.value?.currencyCode || currency,
                                )}
                              </Text>
                            </Group>
                          ))}
                        </div>
                      );
                    })}

                    {/* Divider + grand total */}
                    {grandTotal && (
                      <>
                        <div
                          style={{
                            borderTop:
                              "1px solid var(--mantine-color-default-border)",
                            margin: "4px 0",
                          }}
                        />
                        <Group justify="space-between">
                          <Text fw={700} size="sm">
                            Order Total
                          </Text>
                          <Text fw={700} size="sm">
                            {formatCurrency(
                              parseFloat(grandTotal.amount),
                              grandTotal.currencyCode,
                            )}
                          </Text>
                        </Group>
                      </>
                    )}
                  </Stack>
                </Paper>
              );
            })()}
          </Stack>
        )}
      </Modal>
    </>
  );
}
